/**
 * Mediterranean tile artwork, drawn in code.
 *
 * The rosette is an azulejo-style tile (circles on a grid with an eight-petal
 * flower in each and a four-point star in the gaps). The star is a Moorish
 * eight-point star tile. The frieze is a one-row border strip.
 *
 * Each pattern is turned into an SVG data-URI and published as a CSS variable
 * (--tile-*, --frieze-*), so any stylesheet can use them as backgrounds.
 */

const star8 = (cx, cy, R, r) => {
  const pts = [];
  for (let i = 0; i < 16; i++) {
    const ang = (Math.PI / 8) * i - Math.PI / 2;
    const rad = i % 2 === 0 ? R : r;
    pts.push(`${(cx + rad * Math.cos(ang)).toFixed(2)},${(cy + rad * Math.sin(ang)).toFixed(2)}`);
  }
  return pts.join(' ');
};

const svg = (w, h, body) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${body}</svg>`;

function rosette({ fg, bg, mid, lw = 2.4 }) {
  const flower = (cx, cy) => {
    let s = `<circle cx="${cx}" cy="${cy}" r="48" fill="${bg}" stroke="${fg}" stroke-width="${lw}"/>`;
    s += `<circle cx="${cx}" cy="${cy}" r="41" fill="none" stroke="${mid}" stroke-width="1.2"/>`;
    for (let k = 0; k < 8; k++) {
      const a = k * 45;
      s += `<ellipse cx="${cx}" cy="${cy - 24}" rx="7.5" ry="15" fill="${fg}" transform="rotate(${a} ${cx} ${cy})"/>`;
      s += `<ellipse cx="${cx}" cy="${cy - 24}" rx="2.6" ry="8" fill="${bg}" transform="rotate(${a} ${cx} ${cy})"/>`;
    }
    s += `<polygon points="${star8(cx, cy, 14, 7.5)}" fill="${mid}" stroke="${fg}" stroke-width="1.4"/>`;
    s += `<circle cx="${cx}" cy="${cy}" r="4.5" fill="${fg}"/>`;
    return s;
  };
  let body = `<rect width="100" height="100" fill="${bg}"/>`;
  body += `<path d="M50 30 Q50 50 70 50 Q50 50 50 70 Q50 50 30 50 Q50 50 50 30Z" fill="${fg}"/>`;
  body += `<polygon points="${star8(50, 50, 17, 7)}" fill="${fg}"/>`;
  [[0, 0], [100, 0], [0, 100], [100, 100]].forEach(([x, y]) => { body += flower(x, y); });
  return svg(100, 100, body);
}

function star({ fg, bg, mid, lw = 2.2 }) {
  let s = `<rect width="100" height="100" fill="${bg}"/>`;
  s += `<polygon points="${star8(50, 50, 46, 24)}" fill="${mid}" stroke="${fg}" stroke-width="${lw}" stroke-linejoin="round"/>`;
  s += `<polygon points="${star8(50, 50, 30, 15)}" fill="${bg}" stroke="${fg}" stroke-width="${lw}" stroke-linejoin="round"/>`;
  s += `<circle cx="50" cy="50" r="7" fill="${fg}"/>`;
  [[0, 0], [100, 0], [0, 100], [100, 100]].forEach(([x, y]) => {
    s += `<polygon points="${x},${y - 16} ${x + 16},${y} ${x},${y + 16} ${x - 16},${y}" fill="${fg}"/>`;
    s += `<polygon points="${x},${y - 8} ${x + 8},${y} ${x},${y + 8} ${x - 8},${y}" fill="${bg}"/>`;
  });
  [[50, 0], [100, 50], [50, 100], [0, 50]].forEach(([x, y]) => { s += `<circle cx="${x}" cy="${y}" r="4" fill="${fg}"/>`; });
  return svg(100, 100, s);
}

function frieze({ fg, bg }) {
  // 64 x 32: a star, a diamond and two hairlines
  let s = `<rect width="64" height="32" fill="${bg}"/>`;
  s += `<rect y="3" width="64" height="1.4" fill="${fg}" opacity=".7"/><rect y="27.6" width="64" height="1.4" fill="${fg}" opacity=".7"/>`;
  s += `<polygon points="${star8(16, 16, 10.5, 5)}" fill="${fg}"/>`;
  s += `<circle cx="16" cy="16" r="2.2" fill="${bg}"/>`;
  s += `<polygon points="48,8.5 55.5,16 48,23.5 40.5,16" fill="none" stroke="${fg}" stroke-width="1.6"/>`;
  s += `<circle cx="48" cy="16" r="2" fill="${fg}"/>`;
  return svg(64, 32, s);
}

const uri = (markup) => `url("data:image/svg+xml,${encodeURIComponent(markup)}")`;

/** Publish every tile as a CSS custom property on <html>. Call once at startup. */
export function installTiles() {
  const vars = {
    // Full-strength tile used as a photo frame on light backgrounds
    '--tile-frame':   rosette({ fg: '#094CB0', bg: '#FFFFFF', mid: '#D3E3F9' }),
    // Tone-on-tone fields for the dark blue sections
    '--tile-cobalt':  rosette({ fg: '#2D70D4', bg: '#094CB0', mid: '#0B55C3', lw: 2 }),
    '--tile-sea':     star({ fg: '#1D4C9E', bg: '#072B66', mid: '#082F72', lw: 2 }),
    // Border strips
    '--frieze-cobalt': frieze({ fg: '#FFFFFF', bg: '#094CB0' }),
    '--frieze-sea':    frieze({ fg: '#8DB9F7', bg: '#072B66' }),
    '--frieze-paper':  frieze({ fg: '#094CB0', bg: '#FFFFFF' }),
  };
  const root = document.documentElement;
  Object.entries(vars).forEach(([name, markup]) => root.style.setProperty(name, uri(markup)));
}
